
/**
 * Simple in-memory rate limiter
 * 
 * In a production environment, this should be replaced with a more robust solution 
 * that uses Redis or another distributed cache to track attempts across multiple servers.
 */

interface RateLimitEntry {
  count: number;
  resetAt: number;
}

class RateLimiter {
  private limits: Map<string, RateLimitEntry>;
  private windowMs: number;
  private maxAttempts: number;

  constructor(windowMs = 15 * 60 * 1000, maxAttempts = 5) {
    this.limits = new Map();
    this.windowMs = windowMs;
    this.maxAttempts = maxAttempts;
    
    // Clean up expired entries every minute
    setInterval(() => this.cleanup(), 60 * 1000);
  }

  check(key: string): { allowed: boolean; remainingAttempts: number; resetAt: number | null } {
    this.cleanup();
    
    const now = Date.now();
    const entry = this.limits.get(key);
    
    // If no entry exists or the entry has expired, create a new one
    if (!entry || entry.resetAt < now) {
      const newEntry = {
        count: 1,
        resetAt: now + this.windowMs
      };
      this.limits.set(key, newEntry);
      return { 
        allowed: true, 
        remainingAttempts: this.maxAttempts - 1,
        resetAt: newEntry.resetAt
      };
    }
    
    // If rate limit exceeded
    if (entry.count >= this.maxAttempts) {
      return { 
        allowed: false, 
        remainingAttempts: 0,
        resetAt: entry.resetAt
      };
    }
    
    // Increment attempts
    entry.count += 1;
    this.limits.set(key, entry);
    
    return { 
      allowed: true, 
      remainingAttempts: this.maxAttempts - entry.count,
      resetAt: entry.resetAt
    };
  }
  
  reset(key: string): void {
    this.limits.delete(key);
  }
  
  private cleanup(): void {
    const now = Date.now();
    for (const [key, entry] of this.limits.entries()) {
      if (entry.resetAt < now) {
        this.limits.delete(key);
      }
    }
  }
}

// Create a singleton instance
export const authRateLimiter = new RateLimiter();

// Helper function to format time until reset
export const formatTimeUntilReset = (resetAt: number): string => {
  const seconds = Math.ceil((resetAt - Date.now()) / 1000);
  
  if (seconds < 60) {
    return `${seconds} second${seconds !== 1 ? 's' : ''}`;
  }
  
  const minutes = Math.ceil(seconds / 60);
  return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
};
